/**
 * 宠物战机（P4-FLIGHT）：输入、画面、驾驶舱、暂停、结算的骨架。规则全在 B 的 core（createFlightGame）；这里只把输入交进去、把状态画出来。
 * - 画面：Canvas 2D 画场地、敌机、子弹、慢弹、飞机与飘字；分数、心、波次、连击、暂停、开始页、暂停页、结算页都是 DOM，读屏念得到。
 * - 驾驶舱：场地下面一条，TA 画得够大，跟着规则事件有反应（flightFeedback 的心情，flight.css 的表情动作）；波次、连击和「放绝招」也在这里，
 *   顶栏和贪吃、篮球一样只放分数、心、时间、暂停（多两个标签时 320 宽的屏会把心挤没、把暂停挤出屏幕）。
 *   驾驶员的动作段名等 P 定，定了再接播放器；现在用 TA 的形象图（星球形象 → 物种插画），不贴一个角落小头像。
 * - 输入：在场地上点或拖（飞机飞过去）、方向键或 WASD、触屏左下角的摇杆；空格放绝招；Esc 暂停。飞机自己开火。
 * - 暂停：点暂停、按 Esc、页面切到后台都会暂停；回来停在暂停页，点「继续」才接着玩。
 * - 结算：撑满时间是通关，心用完了提前结束，都出结算页；「不玩了」直接离开，不出结算页、这局不算成绩。成绩只留在这一局里，不存。
 * - 一只宠物一局：换了宠物整个重挂；形象只认这只自己的，不串到别的宠物身上。
 * 失败只是这一局的结果：不说受伤，不写成真实的驾驶经历。接到哪个页面由 I 接线；本组件只要一个 onExit。
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { CharacterState, PetPrivateSummary } from "@/shared/contracts";
import { Button, Icon } from "@/shared/ui";
import { createFlightGame, type FlightConfig, type FlightEvent, type FlightGame as Game, type FlightState } from "../core";
import { readPalette, type Palette } from "./draw";
import { clockText, liveFloats, type FloatText } from "./feedback";
import { FLIGHT_ARENA, drawFlightScene } from "./flightDraw";
import { flightAnnouncementFor, flightFloatsFor, pilotAfter, pilotNow, startingPilot, type Pilot, type PilotMood } from "./flightFeedback";
import { composeInput, isMoveKey, stickFromDrag } from "./input";
import { RESULT_DELAY_MS } from "./SnakeGame";
import { spriteFromCharacter, spriteFromSpecies } from "./sprite";
import { fitArena, toArena, type Fit } from "./viewport";
import "./snake.css";
import "./flight.css";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const JOYSTICK_RADIUS = 44;

export interface FlightGameProps {
  pet: Pick<PetPrivateSummary, "pet_id" | "name" | "species">;
  character?: CharacterState | null;
  seed?: number;
  durationMs?: number;
  onExit: () => void;
  /** 不给就跟随系统“减少动态效果”设置 */
  reducedMotion?: boolean;
  /** 用例与演示台换规则配置用（比如固定出怪表）；产品页不传 */
  configOverrides?: Partial<FlightConfig>;
}

type Hud = { score: number; hearts: number; wave: number; combo: number; special: number; clock: string };

/** 驾驶舱里 TA 心情旁边那个小记号（装饰，读屏不念；不用彩色表情符号，各家手机画得不一样） */
const MOOD_MARK: Record<PilotMood, string> = { calm: "", focus: "", proud: "♪", excited: "!!", nervous: "!?", ouch: "!", ready: "★", special: "★", cleared: "♪", lost: "…" };

function usePrefersReducedMotion(): boolean {
  const query = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION) : null;
  const [reduced, setReduced] = useState(() => query?.matches ?? false);
  useEffect(() => {
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, [query]);
  return reduced;
}

const hudOf = (state: FlightState): Hud => ({ score: state.score, hearts: state.hearts, wave: state.wave, combo: state.combo, special: state.special, clock: clockText(state.remainingMs) });
const sameHud = (a: Hud, b: Hud) => a.score === b.score && a.hearts === b.hearts && a.wave === b.wave && a.combo === b.combo && a.special === b.special && a.clock === b.clock;

/**
 * 一只宠物一局：换了宠物（比如同一个页面换了路由参数）就整个重挂，分数、阶段、飘字、读屏、驾驶舱都不带过去。
 * 同一只宠物重新下发（新对象、改了名字、形象换了一版）不重开，这一局接着玩。
 */
export function FlightGame(props: FlightGameProps) {
  return <FlightRound key={props.pet.pet_id} {...props} />;
}

function FlightRound({ pet, character: givenCharacter = null, seed, durationMs, onExit, reducedMotion, configOverrides }: FlightGameProps) {
  // 只认这只宠物自己的形象：宠物已经换了、形象还是上一只的旧缓存时，先用这只的物种插画
  const character = givenCharacter?.pet_id === pet.pet_id ? givenCharacter : null;
  const systemReduced = usePrefersReducedMotion();
  const still = reducedMotion ?? systemReduced;
  const game = useMemo<Game>(
    () => createFlightGame({ ...configOverrides, seed: seed ?? Date.now() % 1_000_000, ...(durationMs !== undefined ? { durationMs } : {}) }),
    // 一个组件一局；再来一局用同一个对象 restart
    [],
  );
  const [phase, setPhase] = useState(game.state.phase);
  const [hud, setHud] = useState<Hud>(() => hudOf(game.state));
  const [mood, setMood] = useState<PilotMood>("calm");
  const [showResult, setShowResult] = useState(false);
  const [notice, setNotice] = useState<{ seq: number; text: string }>({ seq: 0, text: "" });
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fitRef = useRef<Fit>(fitArena(0, 0, FLIGHT_ARENA.width, FLIGHT_ARENA.height));
  const dprRef = useRef(1);
  const paletteRef = useRef<Palette | null>(null);
  const keysRef = useRef(new Set<string>());
  const stickRef = useRef<{ dx: number; dy: number } | null>(null);
  const targetRef = useRef<{ x: number; y: number } | null>(null);
  const floatsRef = useRef<FloatText[]>([]);
  const floatSeq = useRef(0);
  const pilotRef = useRef<Pilot>(startingPilot);
  const hudRef = useRef(hud);
  const stillRef = useRef(still);
  stillRef.current = still;
  const petNameRef = useRef(pet.name);
  petNameRef.current = pet.name;
  const look = useMemo(() => spriteFromCharacter(character) ?? spriteFromSpecies(pet.species), [character, pet.species]);

  const draw = (nowMs: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d") ?? null;
    if (!canvas || !ctx) return;
    paletteRef.current ??= readPalette(canvas);
    floatsRef.current = liveFloats(floatsRef.current, nowMs);
    drawFlightScene(ctx, { state: game.state, fit: fitRef.current, dpr: dprRef.current, palette: paletteRef.current, floats: floatsRef.current, target: targetRef.current, nowMs, reducedMotion: stillRef.current });
  };

  /** 驾驶舱的心情：一阵反应过了就回平静（攒满绝招时跃跃欲试） */
  const settleMood = (nowMs: number) => {
    const next = pilotNow(pilotRef.current, game.state, nowMs);
    setMood((m) => (m === next ? m : next));
  };

  const handle = (events: FlightEvent[], nowMs: number, lastSeen: ReadonlyMap<string, { x: number; y: number }> = new Map()) => {
    const state = game.state;
    for (const event of events) {
      floatsRef.current.push(...flightFloatsFor(event, state, lastSeen, nowMs, () => ++floatSeq.current));
      const text = flightAnnouncementFor(event, petNameRef.current);
      if (text) setNotice((n) => ({ seq: n.seq + 1, text }));
      pilotRef.current = pilotAfter(event, pilotRef.current, state, nowMs);
    }
    settleMood(nowMs);
    const next = hudOf(state);
    if (!sameHud(next, hudRef.current)) {
      hudRef.current = next;
      setHud(next);
    }
    setPhase(state.phase);
  };

  // 舞台尺寸：等比放下 360×640 的场地，画布按设备像素比出清晰的图
  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!stage || !canvas) return undefined;
    const measure = () => {
      const rect = stage.getBoundingClientRect();
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      dprRef.current = dpr;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      fitRef.current = fitArena(rect.width, rect.height, FLIGHT_ARENA.width, FLIGHT_ARENA.height);
      draw(performance.now());
    };
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(stage);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // 主循环：只在进行中走；每帧先交输入再推进，事件当帧飘字、播报、换心情
  useEffect(() => {
    if (phase !== "running" || typeof window.requestAnimationFrame !== "function") {
      draw(typeof performance !== "undefined" ? performance.now() : 0);
      return undefined;
    }
    let last: number | null = null;
    let handleId = 0;
    const tick = (now: number) => {
      if (last !== null) {
        game.setInput(composeInput({ stick: stickRef.current, keys: keysRef.current, target: targetRef.current }));
        // 推进前记下各敌机的位置：击落的分数飘在那架最后出现的地方
        const lastSeen = new Map(game.state.enemies.map((e) => [e.id, { x: e.x, y: e.y }]));
        handle(game.advance(now - last), now, lastSeen);
      }
      last = now;
      draw(now);
      if (game.state.phase === "running") handleId = window.requestAnimationFrame(tick);
    };
    handleId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(handleId);
  }, [phase]);

  // 结束以后：等一会儿再出结算页（减少动态效果时立刻出）
  useEffect(() => {
    if (phase !== "ended" || !game.state.result?.counted) return undefined;
    if (still || typeof window.requestAnimationFrame !== "function") {
      setShowResult(true);
      return undefined;
    }
    let handleId = 0;
    const startedAt = performance.now();
    const tick = (now: number) => {
      draw(now);
      if (now - startedAt >= RESULT_DELAY_MS) setShowResult(true);
      else handleId = window.requestAnimationFrame(tick);
    };
    handleId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(handleId);
  }, [phase, still]);

  const letGo = () => {
    keysRef.current.clear();
    stickRef.current = null;
    if (knobRef.current) knobRef.current.style.transform = "";
  };
  const pause = () => {
    letGo();
    handle(game.pause(), performance.now());
  };
  const resume = () => handle(game.resume(), performance.now());
  const start = () => handle(game.start(), performance.now());
  const fireSpecial = () => handle(game.special(), performance.now());
  const again = () => {
    setShowResult(false);
    floatsRef.current = [];
    targetRef.current = null;
    letGo();
    handle(game.restart(Date.now() % 1_000_000), performance.now());
  };
  const leave = () => {
    handle(game.quit(), performance.now());
    onExit();
  };

  // 切到后台就暂停；回来停在暂停页
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && game.state.phase === "running") pause();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // 键盘：方向键／WASD 飞，空格放绝招，Esc 暂停
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (game.state.phase !== "running") return;
      if (e.code === "Escape") {
        e.preventDefault();
        pause();
        return;
      }
      if (e.code === "Space") {
        e.preventDefault();
        if (!e.repeat) fireSpecial();
        return;
      }
      if (!isMoveKey(e.code)) return;
      e.preventDefault();
      keysRef.current.add(e.code);
    };
    const up = (e: KeyboardEvent) => keysRef.current.delete(e.code);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", letGo);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", letGo);
    };
  }, []);

  // 场地上点或拖：飞机飞向那个点
  const pointAt = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    targetRef.current = toArena(fitRef.current, e.clientX - rect.left, e.clientY - rect.top);
    stickRef.current = null;
  };
  const onCanvasDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (game.state.phase !== "running") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointAt(e);
  };
  const onCanvasMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (game.state.phase === "running" && e.buttons) pointAt(e);
  };

  // 摇杆：按住拖，松手停
  const knobRef = useRef<HTMLSpanElement>(null);
  const stickAt = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const stick = stickFromDrag(e.clientX - (rect.left + rect.width / 2), e.clientY - (rect.top + rect.height / 2), JOYSTICK_RADIUS);
    stickRef.current = stick;
    targetRef.current = null;
    if (knobRef.current) knobRef.current.style.transform = `translate(${stick.dx * JOYSTICK_RADIUS}px, ${stick.dy * JOYSTICK_RADIUS}px)`;
  };
  const stickRelease = () => {
    stickRef.current = null;
    if (knobRef.current) knobRef.current.style.transform = "";
  };

  const result = game.state.result;
  const hearts = Array.from({ length: Math.max(hud.hearts, 0) });
  const ready = phase === "running" && hud.special >= 1;
  const specialPct = Math.round(Math.min(1, Math.max(0, hud.special)) * 100);
  return (
    <section className="ps-arcade ps-flight" aria-label="宠物战机" data-phase={phase} data-score={hud.score}>
      <header className="ps-arcade__hud">
        <span className="ps-arcade__score" aria-label={`${hud.score} 分`}>{hud.score}<small>分</small></span>
        <span className="ps-arcade__hearts" role="img" aria-label={`还剩 ${hud.hearts} 颗心`}>
          {hearts.map((_, i) => <Icon key={i} name="heart" size={16} />)}
        </span>
        <span className="ps-arcade__clock" aria-label={`还剩 ${hud.clock.replace(":", " 分 ")} 秒`}>{hud.clock}</span>
        <Button variant="ghost" size="sm" icon="pause" aria-label="暂停" onClick={pause} disabled={phase !== "running"} className="ps-arcade__pause" />
      </header>
      <div className="ps-arcade__stage" ref={stageRef}>
        <canvas ref={canvasRef} className="ps-arcade__canvas" role="img" aria-label={`${pet.name}开着飞机，${hud.score} 分`} onPointerDown={onCanvasDown} onPointerMove={onCanvasMove} />
        {phase === "running" ? (
          <div className="ps-arcade__stick" aria-hidden="true" onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); stickAt(e); }} onPointerMove={(e) => { if (e.buttons) stickAt(e); }} onPointerUp={stickRelease} onPointerCancel={stickRelease}>
            <span ref={knobRef} className="ps-arcade__knob" />
          </div>
        ) : null}
        {phase === "ready" ? (
          <div className="ps-arcade__overlay" role="dialog" aria-modal="true" aria-labelledby="ps-flight-title">
            <div className="ps-arcade__sheet">
              <h2 id="ps-flight-title">宠物战机</h2>
              <p className="ps-arcade__line">带{pet.name}开飞机</p>
              {/* 一句一行、每句短到最窄的屏也放得下，不折出孤字 */}
              <ul className="ps-arcade__rules">
                <li>拖着飞机躲开来袭。</li>
                <li>飞机会自己开火。</li>
                <li>连着打中有连击加分。</li>
                <li>攒满绝招，一下清屏。</li>
                <li>撑满时间就是通关。</li>
              </ul>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="play" onClick={start} autoFocus>开始</Button>
                <Button variant="ghost" onClick={onExit}>回去</Button>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "paused" ? (
          <div className="ps-arcade__overlay" role="dialog" aria-modal="true" aria-labelledby="ps-flight-paused">
            <div className="ps-arcade__sheet">
              <h2 id="ps-flight-paused">暂停中</h2>
              <p className="ps-arcade__line">{hud.score} 分 · 还剩 {hud.clock}</p>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="play" onClick={resume} autoFocus>继续</Button>
                <Button variant="ghost" onClick={leave}>不玩了</Button>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "ended" && showResult && result?.counted ? (
          <div className="ps-arcade__overlay is-result" role="dialog" aria-modal="true" aria-labelledby="ps-flight-result">
            <div className="ps-arcade__sheet">
              <h2 id="ps-flight-result">{result.cleared ? "通关了" : "心用完了"}</h2>
              <p className="ps-arcade__total"><strong>{result.score}</strong> 分</p>
              <dl className="ps-arcade__stats">
                <div><dt>击落</dt><dd>{result.kills} 个</dd></div>
                <div><dt>最长连击</dt><dd>{result.bestCombo} 个</dd></div>
                <div><dt>放了绝招</dt><dd>{result.specials} 次</dd></div>
                <div><dt>被撞到</dt><dd>{result.hits} 次</dd></div>
                <div><dt>打到</dt><dd>第 {result.wave} 波</dd></div>
                {result.cleared ? <div><dt>剩下的心</dt><dd>{result.heartsLeft} 颗</dd></div> : null}
              </dl>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="refresh" onClick={again} autoFocus>再来一局</Button>
                <Button variant="ghost" onClick={onExit}>回去</Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {/* 驾驶舱：TA 画得够大、跟着事件有反应；放绝招也在这里 */}
      <div className="ps-flight__cockpit" data-mood={mood}>
        <span className="ps-flight__pilot" aria-hidden="true">
          {look ? <img src={look.url} alt="" draggable={false} /> : <span className="ps-flight__pilot-face" />}
          {MOOD_MARK[mood] ? <span className="ps-flight__mark">{MOOD_MARK[mood]}</span> : null}
        </span>
        <div className="ps-flight__special">
          {/* 波次、连击放在驾驶舱：顶栏和贪吃、篮球一样只放分数、心、时间、暂停，最窄的屏（320）也放得下 */}
          <div className="ps-flight__tags">
            {hud.wave > 0 ? <span className="ps-flight__wave">第 {hud.wave} 波</span> : null}
            {hud.combo >= 2 ? <span className="ps-flight__combo">连击 {hud.combo}</span> : null}
          </div>
          <span className="ps-flight__meter" role="meter" aria-label="绝招" aria-valuemin={0} aria-valuemax={100} aria-valuenow={specialPct}>
            <span style={{ width: `${specialPct}%` }} />
          </span>
          <Button variant={ready ? "primary" : "secondary"} onClick={fireSpecial} disabled={!ready}>放绝招</Button>
        </div>
      </div>
      <p className="visually-hidden" aria-live="polite">
        <span key={notice.seq}>{notice.text}</span>
      </p>
    </section>
  );
}
